import QUESTION_TYPE from "enums/QUESTION_TYPE";
import SCRIPT_STATUS from "enums/SCRIPT_STATUS";
import SCRIPT_TYPE from "enums/SCRIPT_TYPE";
import { produce } from "immer";
import { DateTime } from "luxon";
import ScriptDto from "types/ScriptDto";

export const scope = "ScriptManagement";

type State = {
  script: Partial<ScriptDto>;
  editIndex: number;
  hasScriptChange: boolean;
  hasScriptError: boolean;
};

export const DEFAULT_LANGUAGE_CODE = "en-US";

export const DEFAULT_FORM_STATE: State = {
  script: {
    type: SCRIPT_TYPE.MOBILE,
    status: SCRIPT_STATUS.DRAFT,
    title: "",
    description: "",
    languageCode: DEFAULT_LANGUAGE_CODE,
    startDate: DateTime.now().toISODate(),
    endDate: DateTime.now().plus({ days: 30 }).toISODate(),
    questions: [
      {
        type: QUESTION_TYPE.WELCOME,
        title:
          "Please record your video in a quiet location with sufficient lighting, and ensure that your WiFi connection is active. After recording a video, you have the opportunity to review it and record it again if needed.",
      },
      {
        type: QUESTION_TYPE.CONSENT,
        title:
          "Please state your name and that you are willing to participate in this study.",
      },
      {
        type: QUESTION_TYPE.THANKYOU,
        title:
          "All finished? Please check your WiFi signal, and click on Submit Videos.\nWant to change a response? To return to the survey, click on the Back Arrow. \nWant to complete it later? Click on the Cancel. Your answers are saved so you can pick up where you left off.",
      },
    ],
  },

  editIndex: -1,
  hasScriptChange: false,
  hasScriptError: false,
};

const createScriptStore = (set) => ({
  ...DEFAULT_FORM_STATE,

  setValue: (key, value) => {
    set(
      produce((currentState: State) => {
        currentState[scope][key] = value;
      }),
      false,
      `[${scope}.setValue](${key}, ${JSON.stringify(value)})`,
    );
  },

  addQuestion: (question) => {
    set(
      produce((currentState: State) => {
        const questions = currentState[scope].script.questions;
        currentState[scope].editIndex = questions.length - 1;
        questions.splice(questions.length - 1, 0, question);
      }),
      false,
      `[${scope}.addQuestion](${JSON.stringify(question)})`,
    );
  },

  deleteQuestion: (index: number) => {
    set(
      produce((currentState: State) => {
        currentState[scope].script.questions.splice(index, 1);
      }),
      false,
      `[${scope}.deleteQuestion](${index})`,
    );
  },

  updateQuestion: (question, index: number) => {
    set(
      produce((currentState: State) => {
        currentState[scope].script.questions[index] = question;
        currentState[scope].editIndex = -1;
      }),
      false,
      `[${scope}.updateQuestion](${JSON.stringify(question)})`,
    );
  },

  handleReset: () => {
    set(
      produce((currentState: State) => {
        currentState[scope].script = DEFAULT_FORM_STATE.script;
        currentState[scope].editIndex = DEFAULT_FORM_STATE.editIndex;
        currentState[scope].hasScriptChange =
          DEFAULT_FORM_STATE.hasScriptChange;
      }),
      false,
      `[${scope}.handleReset]`,
    );
  },

  setHasScriptChange: (hasScriptChange: boolean) => {
    set(
      produce((currentState: State) => {
        currentState[scope].hasScriptChange = hasScriptChange;
      }),
      false,
      `[${scope}.setHasScriptChange](${hasScriptChange})`,
    );
  },

  setHasScriptError: (hasScriptError: boolean) => {
    set(
      produce((currentState: State) => {
        currentState[scope].hasScriptError = hasScriptError;
      }),
      false,
      `[${scope}.setHasScriptError](${hasScriptError})`,
    );
  },
});

export default createScriptStore;
